import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Shield, Users, Sparkles, Phone, Mail, MapPin, GraduationCap, Trees, PawPrint, CheckCircle2, ChevronDown, ChevronRight, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { fetchCollection } from '../lib/cms';
import useDocumentMeta from '../hooks/useDocumentMeta';
import ReviewSlider from '../components/ReviewSlider';
import Lightbox from '../components/Lightbox';

export default function Home() {
  useDocumentMeta(
    'Beskyttet beskæftigelse §103 i Brørup | Kontakt os her',
    'Oplev beskyttet beskæftigelse §103 i Brørup med dyr, natur og trygge rammer. LIVSLYST tilbyder nærvær og udvikling i et lille fællesskab.',
    ''
  );

  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  const faqItems = [
    {
      question: "Hvad er beskyttet beskæftigelse?",
      answer: "Beskyttet beskæftigelse er et pædagogisk støttet arbejdsforløb for personer, som ikke kan arbejde på normale vilkår. LIVSLYST tilbyder en tryg og overskuelig arbejdsdag med individuelt tilrettelagte opgaver, faste rammer og plads til seks borgere. Tilbuddet har base i Brørup og er relevant ved søgning efter beskyttet beskæftigelse Brørup."
    },
    {
      question: "Hvem er beskyttet beskæftigelse efter paragraf 103 for?",
      answer: "Beskyttet beskæftigelse efter paragraf 103 er for borgere under folkepensionsalderen, som på grund af betydeligt nedsat fysisk eller psykisk funktionsevne eller særlige sociale problemer ikke kan arbejde på almindelige vilkår. Hos LIVSLYST kan målgruppen blandt andet omfatte personer med kognitive udfordringer, udviklingsforstyrrelser, autisme eller erhvervet hjerneskade."
    },
    {
      question: "Hvad betyder serviceloven 103, og hvordan bliver et forløb bevilget?",
      answer: "Serviceloven 103 er en uformel betegnelse for servicelovens § 103, som er den juridiske ramme for beskyttet beskæftigelse. Det er borgerens bopælskommune, der vurderer behovet og bevilger forløbet. LIVSLYST tager gerne en indledende dialog med borgere, pårørende og fagpersoner om behov, mål og et muligt match."
    },
    {
      question: "Hvad kendetegner et privat beskæftigelsestilbud?",
      answer: "Hos LIVSLYST er et privat beskæftigelsestilbud kendetegnet ved et lille arbejdsfællesskab, genkendelige rammer, tydelig kommunikation og tæt individuel støtte. Arbejdsopgaver og krav tilpasses den enkelte borgers ressourcer, interesser, udviklingsmål og dagsform."
    },
    {
      question: "Hvilke arbejdsopgaver kan indgå i beskyttet beskæftigelse?",
      answer: "Hos LIVSLYST kan arbejdsopgaverne blandt andet bestå af dyrepleje, havearbejde, vedligeholdelse af grønne områder, kreative værkstedsopgaver og andre praktiske udendørsaktiviteter. Opgavernes type, omfang og støtte tilpasses, så deltagelsen bliver realistisk, meningsfuld og udviklende for borgeren."
    },
    {
      question: "Hvordan kan arbejde med dyr og natur støtte trivsel og udvikling?",
      answer: "LIVSLYST bruger dyr og natur som en integreret del af læringsmiljøet. Ansvar for dyr kan understøtte struktur, empati, omsorg og ansvarsfølelse, mens aktiviteter i naturen kan give ro, motivation og et tydeligt formål med arbejdsdagen."
    },
    {
      question: "Hvordan tilpasses støtte og arbejdsopgaver til den enkelte?",
      answer: "LIVSLYST tager udgangspunkt i borgerens ressourcer, interesser, tempo og aktuelle dagsform. Hverdagen understøttes med faste rutiner, visuel støtte, tydelig kommunikation og en Low Arousal-tilgang. Trivsel og progression følges gennem samtaler, individuelle mål og faglig refleksion, så støtten løbende kan justeres."
    },
    {
      question: "Kan man besøge et beskæftigelsestilbud før en beslutning?",
      answer: "Ja. LIVSLYST tilbyder en uforpligtende samtale og rundvisning, hvor borgere, pårørende eller fagpersoner kan høre om hverdagen, målgruppen, arbejdsopgaverne og den pædagogiske tilgang. Her kan man også tale om aktuelle pladser og vurdere, om tilbuddet passer til borgerens behov og kommunale bevilling."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  useEffect(() => {
    async function loadData() {
      const galleryData = await fetchCollection('galleri');
      setGallery(galleryData);
      setLoading(false);
    }
    loadData();
  }, []);

  const previewItems = gallery
    .filter(item => {
      const category = (item.category?.da || item.category || '').toLowerCase();
      const isVideo = item.image?.[0]?.endsWith('.mp4') || category === 'video';
      return !isVideo;
    })
    .slice(0, 3);

  const pillars = [
    {
      title: 'Målgruppe',
      description: 'Voksne borgere med nedsat funktionsevne, som har behov for individuelle hensyn, tryghed og hverdagsstruktur.',
      icon: Users,
      path: '/maalgruppe',
    },
    {
      title: 'Kerneværdier',
      description: 'Vores arbejde bygger på dyb anerkendelse, respekt, fællesskab, ansvar og øget livskvalitet for den enkelte.',
      icon: Heart,
      path: '/vaerdigrundlag',
    },
    {
      title: 'Pædagogik',
      description: 'Høj faglighed med fokus på struktureret pædagogik, Low Arousal (SNA) og neuropædagogiske metoder.',
      icon: Shield,
      path: '/paedagogik',
    },
    {
      title: 'STU',
      description: 'En 3-årig særligt tilrettelagt ungdomsuddannelse med fokus på personlig, social og praktisk-faglig udvikling.',
      icon: GraduationCap,
      path: '/stu',
    },
  ];

  return (
    <div className="bg-stone-50 text-stone-800 antialiased overflow-x-hidden">
      {/* 1. Hero Section */}
      <section className="relative bg-stone-100 py-20 lg:py-32 overflow-hidden border-b border-stone-200">
        {/* Decorative background elements */}
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-emerald-100/50 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-amber-100/50 blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Hero Text */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="lg:col-span-7 space-y-6 text-center lg:text-left"
            >
              <motion.span 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 uppercase tracking-wider"
              >
                Privat Beskyttet Beskæftigelse — §103
              </motion.span>
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight leading-tight"
              >
                Beskyttet beskæftigelse med plads til <span className="text-emerald-800">trivsel</span> og <span className="text-emerald-800">udvikling</span>
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-base sm:text-lg text-stone-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed"
              >
                Hos LIVSLYST ApS skaber vi en varm, struktureret og meningsfuld hverdag for voksne i beskyttet beskæftigelse i Brørup. Med høj pædagogisk faglighed støtter vi den enkelte i at vokse i eget tempo.
              </motion.p>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4 pt-2"
              >
                <Link
                  to="/kontakt"
                  className="px-8 py-4 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200 text-center"
                >
                  Kontakt os
                </Link>
                <Link
                  to="/beskaeftigelsen"
                  className="px-8 py-4 bg-white hover:bg-stone-100 text-stone-800 border border-stone-300 rounded-lg font-semibold shadow-sm hover:shadow transition-all duration-200 text-center"
                >
                  Læs mere
                </Link>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, x: 20 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
              className="lg:col-span-5 relative"
            >
              <div className="absolute inset-0 bg-emerald-800 rounded-2xl rotate-3 scale-95 opacity-10" />
              <img
                src="https://assets.auxodev.dk/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/ae28e25b-a6bd-4308-ad3d-758bb1aedd2c.jpeg"
                alt="LIVSLYST ApS pædagogiske rammer"
                className="relative z-10 w-full h-[350px] sm:h-[450px] object-cover rounded-2xl shadow-xl border-4 border-white"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. Introduktion til LIVSLYST ApS */}
      <section className="py-20 md:py-28 bg-white border-b border-stone-200 relative overflow-hidden" id="intro">
        <div className="absolute top-1/2 left-0 -translate-y-1/2 w-72 h-72 bg-emerald-50/40 rounded-full blur-3xl pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column: Quote card */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="lg:col-span-6 relative"
            >
              <div className="absolute -inset-3 rounded-2xl border border-stone-100 bg-stone-50/50 -rotate-1 pointer-events-none" />
              
              <div className="relative bg-stone-100/80 backdrop-blur-sm p-8 sm:p-10 rounded-2xl border border-stone-200 shadow-sm space-y-6">
                <div className="absolute -top-5 -left-3 w-12 h-12 bg-emerald-700 rounded-xl flex items-center justify-center text-white shadow-md">
                  <span className="text-3xl font-serif leading-none select-none">“</span>
                </div>
                
                <p className="text-xl sm:text-2xl font-bold text-stone-900 tracking-tight leading-relaxed pt-2">
                  Vi tror på, at alle mennesker har brug for at føle sig værdifulde, være en del af et fællesskab og opleve succes i hverdagen.
                </p>
                
                <div className="flex items-center gap-3 pt-2">
                  <div className="w-8 h-[2px] bg-emerald-700" />
                  <span className="text-xs uppercase tracking-wider text-emerald-800 font-bold">
                    Vores Kernefilosofi
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Right Column: Welcoming text */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="lg:col-span-6 space-y-6"
            >
              <div className="space-y-2">
                <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold block">
                  Velkommen til LIVSLYST ApS
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight leading-tight">
                  Trygge rammer bygget på faglighed og omsorg
                </h2>
              </div>
              
              <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
                Beliggende i naturskønne omgivelser i Brørup, tilbyder LIVSLYST ApS en pædagogisk enhed med fokus på privat beskyttet beskæftigelse efter servicelovens §103. Vi kombinerer høj faglighed med en dyb menneskelig omsorg for at skabe de mest trygge og stimulerende rammer for vores borgere.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-stone-100">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-700 shrink-0 mt-0.5">
                    <span className="text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-stone-900">Naturskønne omgivelser</h4>
                    <p className="text-xs text-stone-500">Rolige rammer i Brørup</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-700 shrink-0 mt-0.5">
                    <span className="text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-stone-900">§103 Beskæftigelse</h4>
                    <p className="text-xs text-stone-500">Individuelt tilpasset arbejde</p>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Sektion: Beskyttet beskæftigelse §103 & Arbejdsopgaver med dyr og natur */}
      <section className="py-20 md:py-28 bg-stone-100/70 border-b border-stone-200 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Kort 1: Hvem er beskyttet beskæftigelse for? */}
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="bg-white rounded-3xl p-8 sm:p-10 border border-stone-200 shadow-sm flex flex-col justify-between relative overflow-hidden group hover:shadow-md transition-shadow"
            >
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-800 flex items-center justify-center border border-emerald-100/80">
                    <Shield className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold block">
                      Servicelovens § 103
                    </span>
                    <span className="text-xs text-stone-500 font-medium">
                      Målgruppe & bevillingsgrundlag
                    </span>
                  </div>
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight leading-snug">
                  Hvem er beskyttet beskæftigelse efter paragraf 103 for?
                </h3>

                <div className="space-y-4 text-stone-600 text-base sm:text-lg leading-relaxed">
                  <p>
                    Beskyttet beskæftigelse efter <Link to="/paragraf-103" className="font-semibold text-inherit hover:text-emerald-800 transition-colors">paragraf 103</Link> er for borgere under folkepensionsalderen, som på grund af betydeligt nedsat fysisk eller psykisk funktionsevne eller særlige sociale problemer ikke kan arbejde på normale vilkår. Bopælskommunen vurderer og bevilger forløbet.
                  </p>
                  <p>
                    Målgruppen kan blandt andet omfatte mennesker med kognitive udfordringer, udviklingsforstyrrelser, autisme eller erhvervet hjerneskade. Det afgørende er ikke en bestemt diagnose, men om vores rammer, arbejdsopgaver og støtte passer til borgerens behov og kommunens bevilling.
                  </p>
                </div>
              </div>

              <div className="pt-6 mt-6 border-t border-stone-100 flex items-center justify-between">
                <Link
                  to="/maalgruppe"
                  className="inline-flex items-center gap-2 text-sm font-bold text-emerald-800 hover:text-emerald-900 group/link"
                >
                  <span>Læs mere om vores målgruppe</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                </Link>
              </div>
            </motion.div>

            {/* Kort 2: Arbejdsopgaver med dyr og natur */}
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
              className="bg-white rounded-3xl p-8 sm:p-10 border border-stone-200 shadow-sm flex flex-col justify-between relative overflow-hidden group hover:shadow-md transition-shadow"
            >
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-800 flex items-center justify-center border border-emerald-100/80">
                    <Trees className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold block">
                      Aktiviteter & Læringsmiljø
                    </span>
                    <span className="text-xs text-stone-500 font-medium">
                      Meningsfulde rammer i hverdagen
                    </span>
                  </div>
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight leading-snug">
                  Arbejdsopgaver med dyr og natur giver dagen retning
                </h3>

                <div className="space-y-4 text-stone-600 text-base sm:text-lg leading-relaxed">
                  <p>
                    Dyr og natur er en integreret del af vores læringsmiljø. Opgaver kan være dyrepleje, havearbejde, vedligeholdelse af grønne områder og andre praktiske aktiviteter ude. Vi tilpasser altid opgaven, mængden og støtten til borgerens forudsætninger og dagsform.
                  </p>
                  <p>
                    Arbejdet med dyr kan understøtte struktur, omsorg og ansvarsfølelse, fordi opgaven har et tydeligt formål. Andre borgere finder ro og motivation i naturen eller i kreative værkstedsopgaver. Fælles for aktiviteterne er, at deltagelsen skal opleves realistisk og meningsfuld.
                  </p>
                </div>
              </div>

              <div className="pt-6 mt-6 border-t border-stone-100 flex items-center justify-between">
                <Link
                  to="/beskaeftigelsen"
                  className="inline-flex items-center gap-2 text-sm font-bold text-emerald-800 hover:text-emerald-900 group/link"
                >
                  <span>Læs mere om hverdagen og opgaverne</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                </Link>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* 3. Kort oversigt over målgruppe, værdigrundlag, pædagogik og STU */}
      <section className="py-16 md:py-24 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
              Vores fire søjler
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
              Kort oversigt over vores tilbud
            </h2>
            <p className="text-stone-600 text-base leading-relaxed">
              Udforsk de fire grundlæggende områder, der udgør fundamentet for vores pædagogiske enhed og tilbud.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pillars.map((pillar, idx) => {
              const Icon = pillar.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="bg-white p-8 rounded-2xl border border-stone-200 shadow-sm flex flex-col justify-between h-full"
                >
                  <div className="space-y-4">
                    <div className="p-3 bg-emerald-50 rounded-xl text-emerald-800 inline-block">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-stone-900">{pillar.title}</h3>
                    <p className="text-stone-600 text-sm leading-relaxed">{pillar.description}</p>
                  </div>
                  <div className="pt-6">
                    <Link
                      to={pillar.path}
                      className="inline-flex items-center gap-1.5 text-emerald-800 hover:text-emerald-950 text-sm font-semibold group"
                    >
                      <span>Læs mere</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. Galleri-preview */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center mb-12 gap-4">
            <div className="text-center md:text-left space-y-2">
              <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
                Glimt fra hverdagen
              </span>
              <h3 className="text-3xl font-bold text-stone-900 tracking-tight">
                Vores trygge og inspirerende rammer
              </h3>
            </div>
            <Link
              to="/billeder"
              className="px-6 py-3 bg-stone-100 hover:bg-stone-200 text-stone-800 rounded-lg font-semibold border border-stone-300 transition-colors"
            >
              Se hele galleriet
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-700"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {previewItems.map((item, idx) => (
                <motion.div
                  key={item.id}
                  onClick={() => setLightboxIndex(idx)}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="group relative h-64 rounded-xl overflow-hidden cursor-pointer border border-stone-200 shadow-sm"
                >
                  <img
                    src={item.image?.[0]}
                    alt={item.title?.da || item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-stone-950/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6 text-white">
                    <span className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-1">
                      {item.category?.da || item.category}
                    </span>
                    <h4 className="text-lg font-bold">
                      {item.title?.da || item.title}
                    </h4>
                    <p className="text-xs text-stone-300 mt-1 line-clamp-2">
                      {item.description?.da || item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 5. Anmeldelser/infinite slider */}
      <section className="py-16 md:py-24 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
            <h2 className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
              Udtalelser
            </h2>
            <h3 className="text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
              Hvad siger pårørende og samarbejdspartnere?
            </h3>
            <p className="text-stone-600 text-base leading-relaxed">
              Tryghed og trivsel er vores vigtigste succeskriterier. Her kan du læse udtalelser fra dem, der kender os bedst.
            </p>
          </div>

          <ReviewSlider />
        </div>
      </section>

      {/* Sektion: Oplev beskyttet beskæftigelse i Brørup */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="bg-stone-50/90 rounded-3xl p-8 sm:p-12 lg:p-16 border border-stone-200 shadow-sm relative overflow-hidden"
          >
            {/* Subtle decorative background glow */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-100/40 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-amber-100/30 rounded-full blur-3xl pointer-events-none -ml-20 -mb-20" />

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <div className="lg:col-span-8 space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center border border-emerald-200/60 shadow-xs">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold block">
                      Geografi & Omgivelser
                    </span>
                    <span className="text-xs text-stone-500 font-medium">
                      Brørup • Vejen • Esbjerg • Kolding
                    </span>
                  </div>
                </div>

                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 tracking-tight leading-snug">
                  Oplev beskyttet beskæftigelse i Brørup
                </h3>

                <div className="space-y-4 text-stone-600 text-base sm:text-lg leading-relaxed">
                  <p>
                    Vores tilbud om beskyttet beskæftigelse i Brørup ligger i naturskønne omgivelser og kan være relevant for visiterede borgere fra blandt andet Vejen, Esbjerg og Kolding. Vi har base i Brørup og ingen afdelinger i de nævnte byer.
                  </p>
                  <p>
                    Vil du vide, om LIVSLYST kan være det rette valg, er du velkommen til at kontakte os. Vi inviterer gerne til en uforpligtende samtale og rundvisning, hvor vi kan fortælle mere om hverdagen, målgruppen og vores tilgang til et trygt og meningsfuldt beskæftigelsestilbud.
                  </p>
                </div>

                <div className="pt-2">
                  <span className="text-xs uppercase tracking-wider text-stone-500 font-semibold block mb-2.5">
                    Læs mere om tilbuddet for borgere i nærområdet:
                  </span>
                  <div className="flex flex-wrap gap-2.5">
                    <Link
                      to="/beskyttet-beskaeftigelse-vejen"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white hover:bg-emerald-50 text-stone-700 hover:text-emerald-800 text-xs sm:text-sm font-medium border border-stone-200 hover:border-emerald-200 transition-colors shadow-2xs"
                    >
                      <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Nær Vejen</span>
                      <ChevronRight className="w-3.5 h-3.5 text-stone-400" />
                    </Link>
                    <Link
                      to="/beskyttet-beskaeftigelse-esbjerg"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white hover:bg-emerald-50 text-stone-700 hover:text-emerald-800 text-xs sm:text-sm font-medium border border-stone-200 hover:border-emerald-200 transition-colors shadow-2xs"
                    >
                      <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Nær Esbjerg</span>
                      <ChevronRight className="w-3.5 h-3.5 text-stone-400" />
                    </Link>
                    <Link
                      to="/beskyttet-beskaeftigelse-kolding"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white hover:bg-emerald-50 text-stone-700 hover:text-emerald-800 text-xs sm:text-sm font-medium border border-stone-200 hover:border-emerald-200 transition-colors shadow-2xs"
                    >
                      <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Nær Kolding</span>
                      <ChevronRight className="w-3.5 h-3.5 text-stone-400" />
                    </Link>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-4 flex flex-col justify-center space-y-4 bg-white p-6 sm:p-8 rounded-2xl border border-stone-200 shadow-xs">
                <div className="space-y-2">
                  <span className="text-xs uppercase tracking-wider text-emerald-800 font-bold block">
                    Besøg os i Brørup
                  </span>
                  <p className="text-sm text-stone-600 leading-relaxed">
                    Kom forbi og mærk stemningen. Vi giver gerne en kop kaffe og en rundvisning på gården.
                  </p>
                </div>

                <div className="pt-2 space-y-3">
                  <Link
                    to="/kontakt"
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl font-semibold shadow-xs transition-colors text-sm"
                  >
                    <span>Book en rundvisning</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>

                  <a
                    href="tel:51504654"
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-stone-100 hover:bg-stone-200 text-stone-800 rounded-xl font-semibold transition-colors text-sm border border-stone-200"
                  >
                    <Phone className="w-4 h-4 text-emerald-700" />
                    <span>Ring 51 50 46 54</span>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sektion: Ofte Stillede Spørgsmål (FAQ) */}
      <section className="py-16 md:py-24 bg-stone-50 border-b border-stone-200 relative overflow-hidden" id="faq">
        {/* Schema.org FAQPage structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100/80 text-emerald-800 text-xs font-bold uppercase tracking-wider border border-emerald-200/60 shadow-2xs">
              <HelpCircle className="w-3.5 h-3.5" />
              <span>Ofte stillede spørgsmål</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
              Spørgsmål & svar om beskyttet beskæftigelse
            </h2>
            <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
              Her finder du svar på de mest almindelige spørgsmål om servicelovens § 103, visitation, dagligdagen og vores pædagogiske tilgang.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 items-start">
            {/* Venstre kolonne */}
            <div className="space-y-3 sm:space-y-4">
              {faqItems.slice(0, Math.ceil(faqItems.length / 2)).map((item, index) => {
                const actualIndex = index;
                const isOpen = openFaqIndex === actualIndex;
                return (
                  <motion.div
                    key={actualIndex}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                      isOpen
                        ? 'bg-white border-emerald-300/80 shadow-sm ring-1 ring-emerald-200/50'
                        : 'bg-white/90 border-stone-200/90 hover:border-stone-300 hover:bg-white'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaqIndex(isOpen ? null : actualIndex)}
                      className="w-full px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left gap-4 cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700"
                      aria-expanded={isOpen}
                    >
                      <span className="text-base sm:text-lg font-semibold text-stone-900 leading-snug">
                        {item.question}
                      </span>
                      <span
                        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                          isOpen
                            ? 'bg-emerald-800 text-white rotate-180'
                            : 'bg-stone-100 text-stone-600 group-hover:bg-stone-200'
                        }`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </span>
                    </button>

                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        className="px-5 sm:px-6 pb-5 pt-1 text-stone-600 text-sm sm:text-base leading-relaxed border-t border-stone-100"
                      >
                        <p>{item.answer}</p>
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Højre kolonne */}
            <div className="space-y-3 sm:space-y-4">
              {faqItems.slice(Math.ceil(faqItems.length / 2)).map((item, idx) => {
                const actualIndex = idx + Math.ceil(faqItems.length / 2);
                const isOpen = openFaqIndex === actualIndex;
                return (
                  <motion.div
                    key={actualIndex}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: (idx + 4) * 0.05 }}
                    className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                      isOpen
                        ? 'bg-white border-emerald-300/80 shadow-sm ring-1 ring-emerald-200/50'
                        : 'bg-white/90 border-stone-200/90 hover:border-stone-300 hover:bg-white'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaqIndex(isOpen ? null : actualIndex)}
                      className="w-full px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left gap-4 cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700"
                      aria-expanded={isOpen}
                    >
                      <span className="text-base sm:text-lg font-semibold text-stone-900 leading-snug">
                        {item.question}
                      </span>
                      <span
                        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                          isOpen
                            ? 'bg-emerald-800 text-white rotate-180'
                            : 'bg-stone-100 text-stone-600 group-hover:bg-stone-200'
                        }`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </span>
                    </button>

                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        className="px-5 sm:px-6 pb-5 pt-1 text-stone-600 text-sm sm:text-base leading-relaxed border-t border-stone-100"
                      >
                        <p>{item.answer}</p>
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 6. Kontaktoplysninger og CTA */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="bg-stone-900 text-white rounded-2xl p-8 md:p-12 shadow-xl relative overflow-hidden"
          >
            {/* Decorative background blur */}
            <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-emerald-800/30 blur-3xl" />
            
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7 space-y-4 text-center lg:text-left">
                <h3 className="text-2xl md:text-3xl font-bold tracking-tight">
                  Vil du høre mere om vores tilbud?
                </h3>
                <p className="text-stone-300 text-sm md:text-base leading-relaxed">
                  Vi står altid klar til en uforpligtende snak om visitation, pladser eller en rundvisning i vores trygge rammer i Brørup. Kontakt os i dag, og lad os tage en snak om, hvordan vi kan støtte.
                </p>
              </div>
              <div className="lg:col-span-5 flex flex-col justify-center items-center gap-4">
                <div className="flex flex-col items-center lg:items-start text-xs text-stone-400 space-y-1">
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <Phone className="w-3.5 h-3.5 text-emerald-500" />
                    Tlf: <a href="tel:51504654" className="hover:text-white transition-colors">51 50 46 54</a>
                  </span>
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <Mail className="w-3.5 h-3.5 text-emerald-500" />
                    Email: <a href="mailto:Kontakt@livslystaps.dk" className="hover:text-white transition-colors">Kontakt@livslystaps.dk</a>
                  </span>
                </div>
                <Link
                  to="/kontakt"
                  className="w-full text-center px-6 py-3.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold shadow-md transition-colors"
                >
                  Kontakt os nu
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lightbox for Gallery Preview */}
      {lightboxIndex !== null && (
        <Lightbox
          image={previewItems[lightboxIndex]?.image?.[0]}
          title={previewItems[lightboxIndex]?.title?.da || previewItems[lightboxIndex]?.title}
          description={previewItems[lightboxIndex]?.description?.da || previewItems[lightboxIndex]?.description}
          category={previewItems[lightboxIndex]?.category?.da || previewItems[lightboxIndex]?.category}
          onClose={() => setLightboxIndex(null)}
          onPrev={
            lightboxIndex > 0
              ? () => setLightboxIndex(lightboxIndex - 1)
              : null
          }
          onNext={
            lightboxIndex < previewItems.length - 1
              ? () => setLightboxIndex(lightboxIndex + 1)
              : null
          }
        />
      )}
    </div>
  );
}
